/**
 * COPPA Compliance Page
 * Children's Online Privacy Protection Act Compliance
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  FamilyRestroom,
  ChildCare,
  Security,
  VerifiedUser,
  Block,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function COPPACompliance() {
  const navigate = useNavigate();
  useAuth();

  const lastUpdated = "January 15, 2025";

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/')}
            aria-label="Back to home"
            sx={{ 
              minWidth: 48, 
              minHeight: 48, 
              borderRadius: 1,
              padding: '12px',
              marginRight: '8px',
              '& .MuiSvgIcon-root': {
                fontSize: '24px'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          <FamilyRestroom sx={{ ml: 1, mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            COPPA Compliance
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
            <ChildCare sx={{ mr: 2, verticalAlign: 'middle' }} />
            COPPA Compliance
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Children's Online Privacy Protection Act Compliance
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Last updated: {lastUpdated}
          </Typography>

          {/* COPPA Overview */}
          <Box sx={{ mb: 4 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Important:</strong> Earn-E Chore Helper is fully compliant with the Children's Online Privacy 
                Protection Act (COPPA). We are committed to protecting the privacy and safety of children under 13.
              </Typography>
            </Alert>

            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              1. What is COPPA?
            </Typography>
            <Typography variant="body1" paragraph>
              The Children's Online Privacy Protection Act (COPPA) is a federal law that requires websites and online 
              services to obtain parental consent before collecting, using, or disclosing personal information from 
              children under 13. We take this responsibility seriously.
            </Typography>
          </Box>

          {/* Our Compliance */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <VerifiedUser sx={{ mr: 1 }} />
              2. Our COPPA Compliance
            </Typography>
            
            <Typography variant="body1" paragraph>
              Earn-E Chore Helper is designed with children's privacy in mind:
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="Parent-Controlled Accounts"
                  secondary="Only parents can create accounts and manage family data. Children under 13 cannot create independent accounts."
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="No Direct Data Collection from Children"
                  secondary="All data collection is done through parent accounts with explicit parental consent."
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="Limited Data Collection"
                  secondary="We only collect the minimum information necessary for app functionality."
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="No Third-Party Sharing"
                  secondary="We do not share children's personal information with third parties for commercial purposes."
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="No Advertising to Children"
                  secondary="We do not display advertisements to children or use their data for marketing."
                />
              </ListItem>
            </List>
          </Box>

          {/* Parental Rights */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              3. Parental Rights and Controls
            </Typography>
            
            <Typography variant="body1" paragraph>
              As a parent, you have complete control over your child's data:
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              3.1 Right to Review
            </Typography>
            <Typography variant="body1" paragraph>
              • View all data we have collected about your child
              • Access your child's activity and usage information
              • Review any photos or content your child has uploaded
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              3.2 Right to Delete
            </Typography>
            <Typography variant="body1" paragraph>
              • Request deletion of your child's personal information
              • Remove your child from the family account
              • Delete specific data items (photos, tasks, etc.)
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              3.3 Right to Refuse
            </Typography>
            <Typography variant="body1" paragraph>
              • Opt out of any data collection for your child
              • Disable features that collect additional data
              • Control which family members can see your child's information
            </Typography>
          </Box>

          {/* Data We Collect */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              4. Data We Collect About Children
            </Typography>
            
            <Typography variant="body1" paragraph>
              For children under 13, we only collect the following information with parental consent:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Basic Information:</strong> Child's name and age (managed by parent)</li>
              <li><strong>Task Data:</strong> Chores assigned and completed</li>
              <li><strong>Photos:</strong> Task completion photos (with parental approval)</li>
              <li><strong>Rewards:</strong> Allowance and reward tracking</li>
              <li><strong>Preferences:</strong> App settings and preferences</li>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>We DO NOT collect:</strong> Email addresses, phone numbers, location data, 
                or any other identifying information directly from children.
              </Typography>
            </Alert>
          </Box>

          {/* Restrictions */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Block sx={{ mr: 1 }} />
              5. Features Restricted for Children
            </Typography>
            
            <Typography variant="body1" paragraph>
              To protect children's privacy, certain features are not available to children under 13:
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <Block color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Google Account Integration"
                  secondary="Children cannot connect Google accounts or use Google services"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Block color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Social Features"
                  secondary="No messaging, sharing, or social networking features"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Block color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Third-Party Integrations"
                  secondary="No access to external services or APIs"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Block color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Premium Subscriptions"
                  secondary="Only parents can purchase premium features"
                />
              </ListItem>
            </List>
          </Box>

          {/* Safety Measures */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              6. Safety Measures
            </Typography>
            
            <Typography variant="body1" paragraph>
              We implement multiple safety measures to protect children:
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              6.1 Technical Safeguards
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Secure data encryption in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Limited access to children's data</li>
              <li>Automatic data deletion after account termination</li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              6.2 Administrative Safeguards
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Staff training on COPPA compliance</li>
              <li>Regular privacy policy reviews</li>
              <li>Parental consent verification</li>
              <li>Incident response procedures</li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              6.3 Physical Safeguards
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Secure data centers with restricted access</li>
              <li>Encrypted backups and storage</li>
              <li>Secure disposal of old data</li>
            </Box>
          </Box>

          {/* Parental Consent */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              7. Parental Consent
            </Typography>
            
            <Typography variant="body1" paragraph>
              By creating a family account and adding children, parents provide consent for:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>Collection of the child's basic information</li>
              <li>Use of the child's data for app functionality</li>
              <li>Storage of task completion photos</li>
              <li>Tracking of rewards and allowances</li>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Parents can withdraw consent at any time by contacting us 
                or removing their child from the family account.
              </Typography>
            </Alert>
          </Box>

          {/* Contact for COPPA */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              8. COPPA Contact Information
            </Typography>
            
            <Typography variant="body1" paragraph>
              For questions about our COPPA compliance or to exercise your parental rights:
            </Typography>

            <Typography variant="body1">
              <strong>COPPA Contact:</strong> coppa@earn-echore.app<br />
              <strong>Subject:</strong> COPPA Compliance Inquiry<br />
              <strong>Response Time:</strong> We will respond within 7 business days<br />
              <strong>Phone:</strong> +1-219-280-0438 (for urgent privacy matters)
            </Typography>
          </Box>

          {/* FTC Resources */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              9. Additional Resources
            </Typography>
            
            <Typography variant="body1" paragraph>
              For more information about COPPA and children's online privacy:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>Federal Trade Commission COPPA Rule: <a href="https://www.ftc.gov/enforcement/rules/rulemaking-regulatory-reform-proceedings/childrens-online-privacy-protection-rule" target="_blank" rel="noopener noreferrer">www.ftc.gov/coppa</a></li>
              <li>FTC's Protecting Kids Online: <a href="https://www.consumer.ftc.gov/topics/protecting-kids-online" target="_blank" rel="noopener noreferrer">www.consumer.ftc.gov</a></li>
              <li>Our Privacy Policy: <a href="/privacy-policy">Privacy Policy</a></li>
            </Box>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" align="center">
              <strong>Earn-E Chore Helper is committed to protecting children's privacy and complying with all applicable laws.</strong>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default COPPACompliance;
